import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroBanner from './components/HeroBanner';
import ShowSection from './components/ShowSection';
import { tvShows, categories, hotTags, faqData, siteInfo } from '@/mocks/tvShows';

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const featuredShows = tvShows.filter((s) => s.isRecommended).slice(0, 5);
  const hotShows = tvShows.filter((s) => s.isHot);
  const newShows = tvShows.filter((s) => s.isNew);
  const highRatedShows = tvShows.filter((s) => s.rating >= 9.0).slice(0, 6);
  const guochanShows = tvShows.filter((s) => s.categorySlug === 'guochan').slice(0, 6);
  const hanjuShows = tvShows.filter((s) => s.categorySlug === 'hanju').slice(0, 6);
  const dongmanShows = tvShows.filter((s) => s.categorySlug === 'dongman').slice(0, 6);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://www.fuqicinema.com/',
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.fuqicinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.fuqicinema.com/',
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '夫妻影院首页',
        item: 'https://www.fuqicinema.com/',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <main>
        <HeroBanner featuredShows={featuredShows} />

        <section className="py-6 md:py-8">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                  className="inline-flex items-center px-4 py-2 bg-background-200/70 border border-background-300/50 rounded-full text-sm text-foreground-300 hover:text-accent-500 hover:border-accent-500/40 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <ShowSection
          title="热门推荐"
          subtitle="大家都在看的热播电视剧"
          shows={hotShows}
          viewMoreLink="/category/all"
        />

        <section className="py-8 md:py-12 bg-background-100/50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-5 md:mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading">
                  高分神作
                </h2>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">
                  评分9.0以上，口碑炸裂的经典好剧
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {highRatedShows.map((show) => (
                <Link
                  key={show.id}
                  to={`/detail/${show.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={show.poster}
                      alt={show.title}
                      title={`${show.title} 高分 ${show.category}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-2 right-2 bg-accent-500 text-background-950 text-xs px-2 py-0.5 rounded-md font-bold">
                      {show.rating}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-100 mt-2 line-clamp-1 group-hover:text-accent-500 transition-colors">
                    {show.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <ShowSection
          title="最新上线"
          subtitle="每日更新，追剧不停歇"
          shows={newShows}
          viewMoreLink="/category/dongman"
        />

        <ShowSection
          title="国产剧场"
          subtitle="精选国产好剧，演绎中国故事"
          shows={guochanShows}
          viewMoreLink="/category/guochan"
        />

        <ShowSection
          title="韩剧热播"
          subtitle="最新韩剧同步更新，浪漫与悬疑一网打尽"
          shows={hanjuShows}
          viewMoreLink="/category/hanju"
        />

        <ShowSection
          title="动漫番剧"
          subtitle="热血、修仙、日常，总有一部你喜欢的"
          shows={dongmanShows}
          viewMoreLink="/category/dongman"
        />

        <section className="py-8 md:py-12">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-5">
              热门标签
            </h2>
            <div className="flex flex-wrap gap-2">
              {hotTags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-background-200/50 border border-background-300/40 rounded-full text-sm text-foreground-400 hover:text-accent-500 hover:border-accent-500/40 transition-all duration-200 cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100/50" aria-label="常见问题">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading text-center mb-8">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqData.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-200/50 rounded-lg border border-background-300/40 overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-200 cursor-pointer">
                      {faq.question}
                      <div className="w-5 h-5 flex items-center justify-center ml-3 transition-transform duration-300 group-open:rotate-180">
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-4">
                追剧时光，有你有我
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                夫妻影院汇聚全网热门电视剧资源，国产剧、韩剧、美剧、日剧、动漫一网打尽。
                每日更新最新剧集，高清画质流畅播放，让你随时随地享受追剧的快乐。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}